/**
 * Virtual Variants - Shopify Storefront Snippet
 *
 * This script fetches variant groups from the Virtual Variants app backend
 * and renders them as clickable thumbnails, text buttons, or a dropdown
 * on the product page.
 *
 * Installation:
 * 1. Copy this file to your Shopify theme's assets/ folder
 * 2. Add the Liquid block (see virtual-variants.liquid) to your product template
 * 3. Configure the APP_URL to point to your deployed Virtual Variants app
 *
 * Style settings in this file were baked in at download time from your
 * Virtual Variants Style settings.  To update them, change your settings
 * on the Style page and re-download this file.
 */

(function () {
  'use strict';

  var VirtualVariants = {
    config: {
      appUrl: '',
      shopDomain: '',
      productId: null,
      containerSelector: '#virtual-variants-container',
      displayOverride: null,

      // Style settings baked in at download time by the Install page.
      // To update widget appearance, change settings on the Style page
      // and re-download this file.
      _bakedCss: ".vv-groups { margin: 16px 0; font-family: inherit; }\n.vv-group { margin-bottom: 20px; }\n.vv-group-name { font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; color: currentColor; opacity: 0.7; }\n.vv-thumbnails { display: flex; flex-wrap: wrap; gap: 8px; }\n.vv-thumbnail { position: relative; display: inline-block; width: 60px; height: 60px; border: 1px solid rgba(0,0,0,0.15); border-radius: 4px; overflow: hidden; text-decoration: none; transition: all 0.15s; outline: 1px solid transparent; outline-offset: 0; }\n.vv-thumbnail:hover { opacity: 0.55; }\n.vv-thumbnail.vv-active { border-color: #000000; box-shadow: 0 0 0 2px rgba(0,0,0,0.18); }\n.vv-thumbnail img { width: 100%; height: 100%; object-fit: cover; display: block; }\n.vv-no-image { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.05); font-size: 18px; font-weight: bold; color: currentColor; opacity: 0.5; }\n.vv-thumbnail-wrap { display: inline-flex; flex-direction: column; align-items: center; gap: 4px; }\n.vv-thumbnail-label { font-size: 11px; text-align: center; color: currentColor; opacity: 0.75; max-width: 60px; white-space: normal; overflow-wrap: break-word; word-break: normal; font-family: inherit; }\n.vv-oos-overlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.2); pointer-events: none; }\n.vv-oos-line { width: 85%; height: 85%; color: rgba(0,0,0,0.75); filter: drop-shadow(0 0 1px rgba(255,255,255,0.6)); }\n.vv-buttons { display: flex; flex-wrap: wrap; gap: 8px; }\n.vv-button { display: inline-flex; align-items: center; justify-content: center; min-width: 40px; height: 40px; padding: 0 10px; border: 1px solid rgba(0,0,0,0.2); border-radius: 2px; font-size: 14px; font-weight: 500; text-decoration: none; color: currentColor; background: transparent; transition: all 0.15s; }\n.vv-button:hover { opacity: 0.55; }\n.vv-button.vv-active { border-color: #000000; color: #ffffff; background: #000000; font-weight: 600; }\n.vv-button.vv-oos { position: relative; overflow: hidden; opacity: 0.55; }\n.vv-button.vv-oos::after { content: ''; position: absolute; top: 50%; left: 50%; width: 130%; height: 1.5px; background: currentColor; opacity: 0.7; transform: translate(-50%, -50%) rotate(-35deg); pointer-events: none; }\n.vv-dropdown-wrap { display: inline-block; }\n.vv-dropdown { appearance: auto; padding: 7px 32px 7px 12px; border: 1px solid rgba(0,0,0,0.2); border-radius: 4px; font-size: 14px; font-weight: 500; color: currentColor; background: transparent; cursor: pointer; min-width: 220px; transition: border-color 0.15s; font-family: inherit; }\n.vv-dropdown:focus { outline: none; border-color: #000000; box-shadow: 0 0 0 2px rgba(0,0,0,0.18); }\n.vv-dropdown option.vv-oos { text-decoration: line-through; opacity: 0.6; }\n.vv-color-swatches { display: flex; flex-wrap: wrap; gap: 8px; }\n.vv-color-swatch { display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; border-radius: 0; border: 1px solid rgba(0,0,0,0.15); text-decoration: none; transition: all 0.15s; overflow: hidden; background: #e5e7eb; outline: 1px solid transparent; outline-offset: 2px; }\n.vv-color-swatch:hover { opacity: 0.55; }\n.vv-color-swatch.vv-active { outline-color: #000000; box-shadow: 0 0 0 2px rgba(0,0,0,0.18); }\n.vv-no-color-line { width: 60%; height: 60%; color: rgba(0,0,0,0.3); }",
      showLabel: true,
      labelPosition: "above",
      defaultDisplay: "button",
    },

    init: function (options) {
      this.config.appUrl = options.appUrl || '';
      this.config.shopDomain = options.shopDomain || Shopify.shop || '';
      this.config.containerSelector = options.containerSelector || '#virtual-variants-container';
      // display / style option from the Theme App Extension block setting.
      // When set, it overrides the per-group displayType stored in the API.
      this.config.displayOverride = options.display || options.style || null;

      // Get product ID from Shopify global or from options
      this.config.productId = options.productId
        || (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product && window.ShopifyAnalytics.meta.product.id)
        || (window.meta && window.meta.product && window.meta.product.id)
        || null;

      if (!this.config.productId) {
        console.warn('[VirtualVariants] Product ID not found. Pass productId in options or ensure ShopifyAnalytics is available.');
        return;
      }

      if (!this.config.appUrl) {
        console.warn('[VirtualVariants] appUrl not configured.');
        return;
      }

      // Inject baked styles immediately so the widget is styled before data loads
      this.injectStyles();
      this.fetchAndRender();
    },

    fetchAndRender: function () {
      var self = this;
      var cacheKey = 'vv_' + self.config.productId + '_' + self.config.shopDomain;
      var TTL = 60 * 1000;

      try {
        var raw = sessionStorage.getItem(cacheKey);
        if (raw) {
          var cached = JSON.parse(raw);
          if (cached && typeof cached.ts === 'number' && (Date.now() - cached.ts) < TTL) {
            self.render(cached.groups || []);
            return;
          }
        }
      } catch (e) {}

      var url = self.config.appUrl + '/api/public/variants'
        + '?product_id=' + encodeURIComponent(self.config.productId)
        + '&shop=' + encodeURIComponent(self.config.shopDomain);

      fetch(url, { method: 'GET', headers: { 'Accept': 'application/json' } })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (data) {
          var groups = data.groups || [];
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), groups: groups }));
          } catch (e) {}
          self.render(groups);
        })
        .catch(function (err) {
          console.warn('[VirtualVariants] Failed to load variant groups:', err);
        });
    },

    /**
     * Resolve per-group displayType, applying the global default from
     * merchant style settings and the per-group Theme Editor override (highest priority).
     */
    resolveDisplayType: function (group) {
      // 1. Per-group override from Theme Editor (highest priority)
      if (this.config.displayOverride) return this.config.displayOverride;
      // 2. Per-group displayType from DB
      if (group.displayType) return group.displayType;
      // 3. Global merchant default baked in at download time
      return this.config.defaultDisplay;
    },

    render: function (groups) {
      var container = document.querySelector(this.config.containerSelector);
      if (!container) {
        console.warn('[VirtualVariants] Container not found:', this.config.containerSelector);
        return;
      }

      if (!groups || groups.length === 0) {
        container.style.display = 'none';
        return;
      }

      var currentProductId = String(this.config.productId);
      var html = '';

      for (var i = 0; i < groups.length; i++) {
        var group = groups[i];
        html += this.renderGroup(group, currentProductId);
      }

      container.innerHTML = '<div class="vv-groups">' + html + '</div>';
      this.attachDropdownListeners(container);
    },

    renderGroup: function (group, currentProductId) {
      var displayType = this.resolveDisplayType(group);

      // Label visibility and position — baked in at download time
      var showLabel = this.config.showLabel;
      var labelPosition = this.config.labelPosition;

      var html = '<div class="vv-group" data-group-id="' + group.id + '" data-display-type="' + displayType + '">';

      var nameHtml = '';
      if (group.name && group.name.trim() !== '') {
        nameHtml = '<div class="vv-group-name">' + this.escapeHtml(group.name) + '</div>';
      }

      if (showLabel && nameHtml && labelPosition === 'above') {
        html += nameHtml;
      }

      if (displayType === 'thumbnail') {
        html += '<div class="vv-thumbnails">';
        for (var i = 0; i < group.items.length; i++) {
          var item = group.items[i];
          var isActive = String(item.linkedProductId) === currentProductId;
          var cls = 'vv-thumbnail' + (isActive ? ' vv-active' : '');
          var title = item.label || item.productTitle;
          html += '<div class="vv-thumbnail-wrap">';
          html += '<a class="' + cls + '" href="/products/' + this.escapeHtml(item.productHandle) + '" title="' + this.escapeHtml(title) + '" aria-label="' + this.escapeHtml(title) + '">';
          if (item.productImageUrl) {
            html += '<img src="' + this.escapeHtml(item.productImageUrl) + '" alt="' + this.escapeHtml(title) + '" loading="lazy" />';
          } else {
            html += '<div class="vv-no-image">' + this.escapeHtml(title.charAt(0)) + '</div>';
          }
          if (item.availableForSale === false) {
            html += '<span class="vv-oos-overlay"><svg viewBox="0 0 20 20" class="vv-oos-line"><line x1="2" y1="18" x2="18" y2="2" stroke="currentColor" stroke-width="1.5"/></svg></span>';
          }
          html += '</a>';
          if (showLabel) {
            html += '<span class="vv-thumbnail-label">' + this.escapeHtml(title) + '</span>';
          }
          html += '</div>';
        }
        html += '</div>';
      } else if (displayType === 'dropdown') {
        html += '<div class="vv-dropdown-wrap"><select class="vv-dropdown" data-vv-dropdown>';
        for (var d = 0; d < group.items.length; d++) {
          var dItem = group.items[d];
          var dLabel = dItem.label || dItem.productTitle;
          var dIsActive = String(dItem.linkedProductId) === currentProductId;
          var dOos = dItem.availableForSale === false;
          html += '<option value="/products/' + this.escapeHtml(dItem.productHandle) + '"'
            + (dIsActive ? ' selected' : '')
            + (dOos ? ' class="vv-oos"' : '') + '>'
            + this.escapeHtml(dOos ? dLabel + ' (esaurito)' : dLabel)
            + '</option>';
        }
        html += '</select></div>';
      } else if (displayType === 'color') {
        html += '<div class="vv-color-swatches">';
        for (var ci = 0; ci < group.items.length; ci++) {
          var cItem = group.items[ci];
          var cIsActive = String(cItem.linkedProductId) === currentProductId;
          var cCls = 'vv-color-swatch' + (cIsActive ? ' vv-active' : '');
          var cLabel = cItem.label || cItem.productTitle;
          var cColor = cItem.color || '';
          var cStyle = cColor ? 'background-color:' + this.escapeHtml(cColor) + ';' : '';
          html += '<a class="' + cCls + '" href="/products/' + this.escapeHtml(cItem.productHandle) + '"'
            + ' title="' + this.escapeHtml(cLabel) + '"'
            + ' aria-label="' + this.escapeHtml(cLabel) + '"'
            + (cStyle ? ' style="' + cStyle + '"' : '')
            + '>'
            + (!cColor || cItem.availableForSale === false ? '<svg viewBox="0 0 20 20" class="vv-no-color-line"><line x1="2" y1="18" x2="18" y2="2" stroke="currentColor" stroke-width="2"/></svg>' : '')
            + '</a>';
        }
        html += '</div>';
      } else {
        // button (default)
        html += '<div class="vv-buttons">';
        for (var j = 0; j < group.items.length; j++) {
          var btnItem = group.items[j];
          var btnIsActive = String(btnItem.linkedProductId) === currentProductId;
          var btnOos = btnItem.availableForSale === false;
          var btnCls = 'vv-button' + (btnIsActive ? ' vv-active' : '') + (btnOos ? ' vv-oos' : '');
          var btnLabel = btnItem.label || btnItem.productTitle;
          html += '<a class="' + btnCls + '" href="/products/' + this.escapeHtml(btnItem.productHandle) + '">' + this.escapeHtml(btnLabel) + '</a>';
        }
        html += '</div>';
      }

      if (showLabel && nameHtml && labelPosition === 'below') {
        html += nameHtml;
      }

      html += '</div>';
      return html;
    },

    attachDropdownListeners: function (container) {
      var selects = container.querySelectorAll('[data-vv-dropdown]');
      for (var i = 0; i < selects.length; i++) {
        selects[i].addEventListener('change', function () {
          if (this.value) {
            window.location.href = this.value;
          }
        });
      }
    },

    /**
     * Inject the baked CSS into the page.  Called immediately on init()
     * so the widget is already styled while group data is loading.
     */
    injectStyles: function () {
      var existing = document.getElementById('vv-styles');
      if (existing) existing.parentNode.removeChild(existing);
      var style = document.createElement('style');
      style.id = 'vv-styles';
      style.textContent = this.config._bakedCss;
      document.head.appendChild(style);
    },

    escapeHtml: function (str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  };

  // Expose to global scope
  window.VirtualVariants = VirtualVariants;

})();